import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ColDef, ICellRendererParams, IFilterParams, ValueFormatterParams } from 'ag-grid-community';
import { useSetRecoilState } from 'recoil';

import { EditLined, PauseLined, HourglassFullFilled } from '@tag/tag-icons';

import { Tooltip } from '@tag/tag-components-react-v4';

import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';
import { NumberUtils } from '@/utils/numbers.utils';
import { variableLinesValidator } from '../validation/validator';
import { dateCellRenderer, getCustomerLabel, getSpreadCustomer, isEditableVariableContractLine } from './helper';
import { VARIABLE_STATES_COLORS } from '@/config/statuses.config';
import { Pill } from '@/components/common/Pill/Pill';
import HeaderCheckbox from '@/components/common/TableGrid/CustomComponents/AgGridCheckbox/AgGridHeaderCheckbox';
import RowCheckbox from '@/components/common/TableGrid/CustomComponents/AgGridCheckbox/AgGridRowCheckbox';
import {
  BillingTypeEnum,
  ProductOutputType,
  PurchaseOrderOutputType,
  PurchaseOrderStatusEnum,
  VariableContractLineOutput,
  ContractStatusEnum,
  ContractBillingStatusPascalCaseEnum,
} from '@/graphql/contracts/_generated_/contracts';


import { AgGridSelectableFilterOwnProps }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import { ContractLineFilterOptions }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterOptions';
import { ContractLineFilterComparators }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterComparators';
import ModalConfirmation from '../components/ModalConfirmation/ModalConfirmation';
import { useModalWindowApi } from '@/components/ModalWindowApi/useModalWindowApi';
import { getBillingIntervals } from '@/utils/data-helpers/intervals';
import useGetProducts from '../hooks/useGetProducts';
import { STOCK_CODE_SEARCH_RESULTS_TO_SHOW } from '@/config/common.config';
import { useUpdateFullVariableContractLine } from '../hooks/useUpdateFullVariableContractLine';
import { ContractLineCommonData } from '@/graphql/contracts/_generated_/ContractLineCommonData.fragment';
import {
  BulkUpdateContractLineInvoiceToCustomerVariables,
  BulkUpdateContractLineInvoiceToCustomer
} from '@/graphql/contracts/_generated_/bulkUpdateContractLineInvoiceToCustomer.mutation';
import {
  BulkUpdateContractLineShipToCustomerVariables,
  BulkUpdateContractLineShipToCustomer
} from '@/graphql/contracts/_generated_/bulkUpdateContractLineShipToCustomer.mutation';
import { PaginatedFilteredPurchaseOrders } from '../hooks/useGetPurchaseOrders';
import { rightsidePanel_isOpen } from '../../RightPanel/RightPanel.state';
import { clearLinesSelection } from '../components/ContractLinesGrid/linesSelectionObservable';
import { clearVariableLinesSelection } from '../components/ContractLinesGrid/variableLinesSelectionObservable';
import { UserRole } from '@/config/roles.config';

export enum ContractStatusFormatted {
  AwaitingApproval = 'Awaiting Approval',
  Active = 'Active',
  Inactive = 'Inactive',
  ActiveDNR = 'DNR',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
  Terminating = 'Terminating',
  Terminated = 'Terminated',
  Merged = 'Merged'
}

interface IUseVariableColumnsProps {
  searchShipToCustomer:
  | {
    searchCustomers: (text: string) => Promise<(ContractLineCommonData['shipToCustomer'] | undefined | null)[]>
    isSearchLoading: boolean;
    searchCustomersData: Array<
    {
      customerID: string;
      customerName: string;
      customerAccountNumber: string;
      address?: string | null;
      postCode?: string | null;
    } & { __typename: 'CustomerType' }
    >;
  };
  searchInvoiceToCustomer:
  | {
    searchCustomers: (text: string) => Promise<(ContractLineCommonData['invoiceToCustomer'] | undefined | null)[]>;
    isSearchLoading: boolean;
    searchCustomersData: Array<
    {
      customerID: string;
      customerName: string;
      customerAccountNumber: string;
      address?: string | null;
      postCode?: string | null;
    } & { __typename: 'CustomerType' }
    >;
  };
  searchPurchaseOrders: {
    getPurchaseOrdersItems: (params: PaginatedFilteredPurchaseOrders) => Promise<(({
      id?: string | null;
      customerId?: string | null;
      customerName?: string | null;
      customerCode?: string | null;
      poNumber?: string | null;
      poDate?: any | null;
      poStartDate?: any | null;
      poEndDate?: any | null;
      poTotalValue?: string | null;
      poStatus?: PurchaseOrderStatusEnum | null;
      poNote?: string | null;
    } & {
    }) | null)[]>
  }
  bulkUpdateContractLineShipToCustomer: (
    params: BulkUpdateContractLineShipToCustomerVariables,
    isVariable: boolean,
    shouldRefetchCls: boolean
  ) => Promise<BulkUpdateContractLineShipToCustomer['bulkUpdateContractLineShipToCustomer'] | undefined>;
  bulkUpdateContractLineInvoiceToCustomer: (
    params: BulkUpdateContractLineInvoiceToCustomerVariables,
    isVariable: boolean,
    shouldRefetchCls: boolean
  ) => Promise<BulkUpdateContractLineInvoiceToCustomer['bulkUpdateContractLineInvoiceToCustomer'] | undefined>;
  bulkAssignPurchaseOrder: (
    purchaseOrderId: string,
    contractLineIds: string[],
    handleSuccess?: () => void
  ) => void;
  bulkUnassignPurchaseOrder: (
    contractLineIds: string[],
    handleSuccess?: () => void
  ) => void;
  onOpenPauseContractLineModal?: (
    contractLine: VariableContractLineOutput,
    isVariableLines?: boolean
  ) => void;
  onOpenDnrModal?: (
    contractLine: VariableContractLineOutput,
    isVariableLines?: boolean
  ) => void;
  userRole?: UserRole;
}

export function useVariableColumns({
  searchShipToCustomer,
  searchInvoiceToCustomer,
  searchPurchaseOrders,
  bulkUpdateContractLineShipToCustomer,
  bulkUpdateContractLineInvoiceToCustomer,
  bulkAssignPurchaseOrder,
  bulkUnassignPurchaseOrder,
  onOpenPauseContractLineModal,
  onOpenDnrModal,
  userRole
}: IUseVariableColumnsProps): ColDef<VariableContractLineOutput>[] {
  const { contractId }: { contractId: UUID } = useParams();
  const contract = useGetContractDetailsFromCache(contractId);
  const updateVariableLine = useUpdateFullVariableContractLine(contractId);
  const { addModal } = useModalWindowApi();
  const { getProducts } = useGetProducts();
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isOneEvoMigrated = isContractOneEvoMigrated(contract);
  const isRestrictedRole = isUserRole || isReaderRole || isOneEvoMigrated;
  const isEditableForUser = (params: any) => !isRestrictedRole && isEditableVariableContractLine(params);

  return useMemo<ColDef<VariableContractLineOutput>[]>(() => ([
    {
      colId: 'grid-checkbox',
      headerName: '',
      headerComponent: HeaderCheckbox,
      minWidth: 50,
      maxWidth: 50,
      width: 50,
      flex: 0,
      resizable: false,
      sortable: false,
      cellClass: 'p-0',
      headerClass: 'p-0',
      lockPosition: true,
      pinned: 'left',
      lockPinned: true,
      suppressSizeToFit: true,
      cellRenderer: RowCheckbox,
    },
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 140,
      lockPosition: true,
      resizable: false,
      pinned: 'left',
      suppressSizeToFit: true,
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      filter: 'SelectableFilter',
      filterParams: (): AgGridSelectableFilterOwnProps<VariableContractLineOutput> => ({
        optionsOrGetterFunc: ContractLineFilterOptions.getVariableStatusFilterOptions,
        comparator: ContractLineFilterComparators.statusVariableComparator,
        rerenderOnNewRowsLoaded: true,
      }),
      cellRenderer: ({
        value,
        data
      }: ICellRendererParams<
      VariableContractLineOutput,
      VariableContractLineOutput['status']
      >) => {
        const color = value && VARIABLE_STATES_COLORS[value];

        if (value === ContractStatusEnum.ActiveDNR && data?.statusChangeDate) {
          const dnrDate = new Date(data.statusChangeDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });

          const handleDnrClick = () => {
            if (data && onOpenDnrModal) {
              onOpenDnrModal(data, true);
            }
          };

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background={color ?? undefined}
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleDnrClick}
                    >
                      {value && ContractStatusFormatted[value]}
                    </Pill>
                  </span>
                }
              >
                DNR Date: {dnrDate} - Click to view details
              </Tooltip>
            </div>
          );
        }

        return (
          <Pill background={color ?? undefined} size="large">
            {value && ContractStatusFormatted[value]}
          </Pill>
        );
      },
    },
    ...([{
      colId: 'pauseStatus',
      headerName: 'Pause Status',
      field: 'lineBillingStatus',
      autoHeight: true,
      maxWidth: 140,
      lockPosition: true,
      resizable: false,
      pinned: 'left' as const,
      suppressSizeToFit: true,
      filter: 'SelectableFilter',
      filterParams:
        (): AgGridSelectableFilterOwnProps<VariableContractLineOutput> => ({
          optionsOrGetterFunc:
            ContractLineFilterOptions.getPauseStatusVariableFilterOptions,
          comparator: ContractLineFilterComparators.pauseStatusVariableComparator,
          rerenderOnNewRowsLoaded: true,
        }),
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      cellRenderer: ({
        data
      }: ICellRendererParams<VariableContractLineOutput>) => {
        // Only show for Active, ActiveDNR, and Terminating statuses
        if (
          data?.status != ContractStatusEnum.Active &&
          data?.status != ContractStatusEnum.ActiveDNR &&
          data?.status != ContractStatusEnum.Terminating
        ) {
          return null;
        }

        const status = data?.lineBillingStatus;

        const handleEditClickForActive = () => {
          if (data && onOpenPauseContractLineModal) {
            onOpenPauseContractLineModal(data, true);
          }
        };

        // Show pause icon for Paused status
        if (status === ContractBillingStatusPascalCaseEnum.Paused) {
          const startDate = data?.lineBillingCurrentStatusStartDate
            ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          const endDate = data?.lineBillingCurrentStatusEndDate
            ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#9C27B0"
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleEditClickForActive}
                    >
                      Paused <PauseLined style={{ fill: 'white', height: '20px' }} />
                    </Pill>
                  </span>
                }
              >
                Paused - Click to view details
                <br />
                Start Date: {startDate}
                <br />
                End Date: {endDate}
              </Tooltip>
            </div>
          );
        }

        // Show hourglass icon for FuturePause status
        if (status === ContractBillingStatusPascalCaseEnum.FuturePause) {
          const startDate = data?.lineBillingCurrentStatusStartDate
            ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          const endDate = data?.lineBillingCurrentStatusEndDate
            ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
            : 'N/A';
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#00BCD4"
                      size="large"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleEditClickForActive}
                    >
                      Future Pause <HourglassFullFilled style={{ fill: 'white', height: '20px' }} />
                    </Pill>
                  </span>
                }
              >
                Future Pause - Click to view details
                <br />
                Start Date: {startDate}
                <br />
                End Date: {endDate}
              </Tooltip>
            </div>
          );
        }

        // Show edit icon for On/Active status
        if (status === ContractBillingStatusPascalCaseEnum.On) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                tooltipFor={
                  <span>
                    <Pill
                      background="#2196F3"
                      size="large"
                      style={{
                        cursor: isRestrictedRole ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      onClick={isRestrictedRole ? undefined : handleEditClickForActive}
                    >
                      Off {!isRestrictedRole && <EditLined style={{ fill: 'white', height: '20px' }} />}
                    </Pill>
                  </span>
                }
              >
                {isRestrictedRole ? 'Off' : 'Off - Click to pause'}
              </Tooltip>
            </div>
          );
        }

        return null;
      },
    }] as ColDef<VariableContractLineOutput>[]),
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      initialWidth: 350,
      minWidth: 250,
      editable: isEditableForUser,
      cellEditor: 'ProductDropdownEditorAsync',
      cellEditorParams: ({ data }: ICellRendererParams) => ({
        handleInputChange: getProducts,
        searchResults: STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
        billingTypes: [BillingTypeEnum.Variable],
        getOptionValue: (option: ProductOutputType) => option.stockCode,
        getOptionLabel: (option: ProductOutputType) => `${option.stockCode} - ${option.stockName}`,
        placeholder: `${data.stockCode} - ${data.stockName}`,
        saveOnChange: true,
        autofocus: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: variableLinesValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueSetter: ({ data, newValue }) => {
        const product = (newValue as ProductOutputType);

        updateVariableLine(data, {
          productId: product.iD!
        });

        return false;
      },
      cellRenderer: ({ data }: ICellRendererParams) => {
        return `${data.stockCode} - ${data.stockName}`;
      }
    },
    {
      colId: 'unitType',
      headerName: 'Unit Type',
      field: 'unitType',
      initialWidth: 120,
      minWidth: 120,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        if (newValue?.trim()?.length || data?.unitType) {
          data.unitType = newValue;
          updateVariableLine(data, {
            unitType: newValue,
          });
        }
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: variableLinesValidator.getErrorsAt(colDef!.field!, data),
      }),
      valueParser: ({ newValue }) => {
        return typeof newValue === 'string' && newValue.length === 0
          ? null
          : newValue;
      },
    },
    {
      colId: 'unitPrice',
      headerName: 'Unit Price',
      field: 'unitPrice',
      initialWidth: 130,
      minWidth: 130,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        data.unitPrice = newValue;

        updateVariableLine(data, {
          unitPrice: newValue
        });

        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: variableLinesValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueParser: ({ newValue }) => {
        const unitPrice = NumberUtils.parseNumericInput(String(newValue));
        return isNaN(unitPrice) ? newValue : unitPrice;
      },
      valueFormatter: ({ value }: ValueFormatterParams<VariableContractLineOutput>) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'billingType',
      headerName: 'Billing Type',
      field: 'billingType',
      initialWidth: 170,
      minWidth: 160,
      valueFormatter: ({
        value
      }: ValueFormatterParams<
      VariableContractLineOutput,
      VariableContractLineOutput['billingType']
      >) => {
        return value || '';
      }
    },
    {
      colId: 'billingInterval',
      headerName: 'Billing Interval',
      field: 'billingInterval',
      initialWidth: 170,
      minWidth: 150,
      editable: isEditableForUser,
      cellEditorParams: ({ data }: { data: VariableContractLineOutput }) => {
        const isLinePaused = (
          data?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
          data?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
        );

        return {
          options: getBillingIntervals(),
          getOptionValue: (option: BillingInterval): string => option.billingInterval,
          getOptionLabel: (option: BillingInterval): string => option.formattedBillingInterval,
          saveOnChange: true,
          handleChangeOption: (
            selectedOption: BillingInterval,
            applyChanges: (selectedOption: BillingInterval) => void,
            cancelChanges: () => void
          ) => {
            addModal({
              modalType: 'confirmation',
              title: 'Please confirm you wish to continue',
              onCancelButtonParams: {
                callback: cancelChanges
              },
              onConfirmButtonParams: {
                callback: () => applyChanges(selectedOption)
              },
              ModalContent: () => <ModalConfirmation
                textImpact={'This change could impact Variable Contract Line\'s predicted dates calculation.'}
                fieldName="billing interval"
                hasPauseActive={isLinePaused} />
            });

          }
        };
      },
      valueSetter: ({ data, newValue }: { data: VariableContractLineOutput, newValue: BillingInterval }) => {
        data.billingInterval = newValue.formattedBillingInterval;
        updateVariableLine(data, {
          billingInterval: newValue.billingInterval,
        });

        return true;
      },
      cellEditor: 'DropdownEditor',
      cellRenderer: 'CellRenderer',
      filter: 'SelectableFilter',
      filterParams: ({ api }: IFilterParams): AgGridSelectableFilterOwnProps<VariableContractLineOutput> => ({
        optionsOrGetterFunc: ContractLineFilterOptions.getBillingIntervalsVariableOptions(api),
        comparator: ContractLineFilterComparators.billingIntervalVariableComparator
      }),
    },
    {
      colId: 'salesHandoverNumber',
      headerName: 'Sales Handover Number',
      field: 'salesHandoverNumber',
      initialWidth: 190,
      minWidth: 150,
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        if (newValue?.trim()?.length || data.salesHandoverNumber) {
          data.salesHandoverNumber = newValue;
          updateVariableLine(data, {
            salesHandoverNumber: newValue
          });
        }
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: variableLinesValidator.getErrorsAt(colDef!.field!, data),
      }),
      valueParser: ({ newValue }) => {
        return typeof newValue === 'string' && newValue.length === 0 ? null : newValue;
      }
    },
    {
      colId: 'minCharge',
      headerName: 'Min Charge',
      field: 'minCharge',
      initialWidth: 130,
      minWidth: 130,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: variableLinesValidator.getErrorsAt(colDef?.field!, data),
      }),
      valueParser: ({ newValue }) => {
        const minCharge = NumberUtils.parseNumericInput(String(newValue));
        return isNaN(minCharge) ? newValue : minCharge;
      },
      valueFormatter: ({ value }: ValueFormatterParams<VariableContractLineOutput>) => {
        return typeof value === 'number' ? NumberUtils.prettifyWithRound(value, 3, ',', 2) : null;
      },
      editable: isEditableForUser,
      valueSetter: ({ data, newValue }) => {
        data.minCharge = newValue || newValue === 0 ? newValue : null;
        updateVariableLine(data, {
          minCharge: (newValue || newValue === 0) ? newValue : null
        });
        return true;
      },
    },
    {
      colId: 'lastSOQuantity',
      headerName: 'Last SO Quantity',
      field: 'lastSOQuantity',
      initialWidth: 190,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: ({ value }: ICellRendererParams) => {
        return NumberUtils.prettifyWithRound(value, 3, ',', 0) ?? '';
      },
    },
    {
      colId: 'lastSOTotalValue',
      headerName: 'Last SO Total Value',
      field: 'lastSOTotalValue',
      initialWidth: 190,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: ({ value }: ICellRendererParams) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      colId: 'lastSOStartDate',
      headerName: 'Last SO Start Date',
      field: 'lastSOStartDate',
      initialWidth: 190,
      minWidth: 150,
      cellRenderer: dateCellRenderer,
    },
    {
      colId: 'lastSOEndDate',
      headerName: 'Last SO End Date',
      field: 'lastSOEndDate',
      initialWidth: 190,
      minWidth: 150,
      cellRenderer: dateCellRenderer,
    },
    {
      colId: 'predictedSOStartDate',
      headerName: 'Predicted SO Start Date',
      field: 'predictedSOStartDate',
      initialWidth: 210,
      minWidth: 200,
      cellRenderer: dateCellRenderer,
    },
    {
      colId: 'predictedSOEndDate',
      headerName: 'Predicted SO End Date',
      field: 'predictedSOEndDate',
      initialWidth: 200,
      minWidth: 190,
      cellRenderer: dateCellRenderer,
    },
    {
      colId: 'predictedAnnualValue',
      headerName: 'Predicted Annual Value',
      field: 'predictedAnnualValue',
      initialWidth: 190,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      cellRenderer: ({ value }: ICellRendererParams) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      colId: 'predictedSOInvoiceDate',
      headerName: 'Predicted SO Invoice Date',
      field: 'predictedSOInvoiceDate',
      initialWidth: 210,
      minWidth: 200,
      cellRenderer: dateCellRenderer,
    },
    ...([
      {
        colId: 'shipToCustomer',
        headerName: 'Ship To Account',
        field: 'shipToCustomerId',
        initialWidth: 350,
        minWidth: 250,
        editable: isEditableForUser,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchShipToCustomer.searchCustomers,
          getOptionValue: (option: ContractLineCommonData['shipToCustomer'] | undefined | null) => option,
          getOptionLabel: (option: ContractLineCommonData['shipToCustomer'] | undefined | null) =>
            getCustomerLabel(option),
          placeholder: getCustomerLabel(getSpreadCustomer(data, 'shipToCustomer')),
          saveOnChange: true,
          autofocus: true,
          isClearable: true,
        }),
        valueSetter: ({ data, newValue }) => {

          const shipToCustomer = newValue as ContractLineCommonData['shipToCustomer'] | undefined | null;

          if (shipToCustomer) {
            data.shipToCustomerId = shipToCustomer.customerID;
            data.shipToCustomerAccountNumber = shipToCustomer.customerAccountNumber;
            data.shipToCustomerName = shipToCustomer.customerName;
            data.shipToCustomerAddress = shipToCustomer.address;
            data.shipToCustomerPostcode = shipToCustomer.postCode;

            bulkUpdateContractLineShipToCustomer({
              shipToCustomerId: shipToCustomer.customerID,
              contractLineIds: [data.iD!],
            }, true, true);
          }

          return true;
        },
        cellRenderer: ({ data }: ICellRendererParams) => {
          return (
            <span className="c-ship-to">
              {getCustomerLabel(getSpreadCustomer(data, 'shipToCustomer'))}
            </span>
          );
        },
      },
      {
        colId: 'invoiceToCustomer',
        headerName: 'Invoice To Account',
        field: 'invoiceToCustomer.customerId',
        initialWidth: 350,
        minWidth: 250,
        editable: isEditableForUser,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchInvoiceToCustomer.searchCustomers,
          getOptionValue: (option: ContractLineCommonData['invoiceToCustomer'] | undefined | null) => option,
          getOptionLabel: (option: ContractLineCommonData['invoiceToCustomer'] | undefined | null) =>
            getCustomerLabel(option),
          placeholder: getCustomerLabel(getSpreadCustomer(data, 'invoiceToCustomer')),
          saveOnChange: true,
          autofocus: true,
        }),
        valueSetter: ({ data, newValue }) => {
          const invoiceToCustomer = newValue as ContractLineCommonData['invoiceToCustomer'] | undefined | null;

          if (invoiceToCustomer) {
            data.invoiceToCustomerId = invoiceToCustomer.customerID;
            data.invoiceToCustomerAccountNumber = invoiceToCustomer.customerAccountNumber;
            data.invoiceToCustomerName = invoiceToCustomer.customerName;
            data.invoiceToCustomerAddress = invoiceToCustomer.address;
            data.invoiceToCustomerPostcode = invoiceToCustomer.postCode;

            bulkUpdateContractLineInvoiceToCustomer({
              invoiceToCustomerId: invoiceToCustomer.customerID,
              contractLineIds: [data.iD!],
            }, true, true);
          }

          return true;
        },
        cellRenderer: ({ data }: ICellRendererParams) => {
          return (
            <span className="c-invoice-to">
              {getCustomerLabel(getSpreadCustomer(data, 'invoiceToCustomer'))}
            </span>
          );
        },
      },
      {
        colId: 'poNumber',
        headerName: 'PO Number',
        field: 'poNumber',
        initialWidth: 200,
        minWidth: 200,
        editable: (params: any) => !isReaderRole && !isOneEvoMigrated && isEditableVariableContractLine(params),
        cellRenderer: ({ data }: ICellRendererParams) => {
          return data.poNumber;
        },
        cellEditor: 'PurchaseOrderDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchPurchaseOrders.getPurchaseOrdersItems,
          customerId: data.invoiceToCustomerId,
          customerCode: data.invoiceToCustomerAccountNumber,
          customerName: data.invoiceToCustomerName,
          currency: contract?.currency.currency,
          getOptionValue: (
            option:
            PurchaseOrderOutputType
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | PurchaseOrderOutputType
            | undefined
            | null
          ) => option?.poNumber,
          placeholder: data.poNumber,
          saveOnChange: true,
          autofocus: true,
        }),
        valueSetter: ({ data, newValue }) => {
          if (newValue && !(newValue as any).__cleared) {
            bulkAssignPurchaseOrder(newValue.id, [data.iD!], () => {
              clearLinesSelection();
              clearVariableLinesSelection();
              setIsOpenRightPanel(false);
            });
          }
          else if (newValue && (newValue as any).__cleared) {
            bulkUnassignPurchaseOrder([data.iD!], () => {
              clearLinesSelection();
              clearVariableLinesSelection();
              setIsOpenRightPanel(false);
            });
          }
          return true;
        }
      },
      {
        colId: 'poStatus',
        headerName: 'PO Status',
        field: 'poStatus',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: ({ value }: ICellRendererParams<ContractLine, ContractLine['poStatus']>) =>
          !!value && value !== 'Active' ? <span>{'\u26A0\uFE0F ' + value}</span> : <span>{value}</span>,
      },
      {
        colId: 'oppHeaderId',
        headerName: 'Opp Header Id',
        field: 'oppHeaderId',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
      {
        colId: 'oppLineId',
        headerName: 'Opp Line Id',
        field: 'oppLineId',
        initialWidth: 190,
        minWidth: 150,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
      {
        colId: 'bundleDescription',
        headerName: ' ',
        field: 'bundleDescription',
        rowGroup: true,
        hide: true,
        sort: 'asc',
        autoHeight: true,
      },
      {
        colId: 'iD',
        headerName: ' ID',
        field: 'iD',
        initialWidth: 300,
        editable: false,
        cellRenderer: 'CellRenderer',
      },
    ] as ColDef<VariableContractLineOutput>[]),
    {
      colId: 'origin',
      headerName: 'Origin',
      field: 'origin',
      initialWidth: 130,
      editable: false,
      cellRenderer: 'CellRenderer',
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [contract]);
}
